import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { rawFetch } from "@/lib/api/client";
import { EmptyState, ErrorBox, StatusPill } from "@/components/common";
import { DiscoveryDraftPanel } from "./DiscoveryDraftPanel";

type ReviewStatus = "proposed" | "source_conflict" | "approved" | "rejected" | "superseded";
type Proposal = {
  id: string; workspace: string; kind: string; status: ReviewStatus;
  normalized_claim: { name?: string; definition?: string; class?: string; property?: string };
  source_locator: string; redacted_quote: string; evidence_withheld: boolean;
  revision: number; change_proposal_id: string | null;
  revisions?: Array<{ revision: number; action: string; reason: string; created_at: string }>;
};
type Queue = { count: number; results: Proposal[] };
type Decision = "edit" | "approve" | "reject" | "resolve_conflict";
type Mapping = { pack_id: string; pack_digest: string; concept_iri: string; label: string;
  pack_name: string; relation: "exact" | "close" | "broader" | "narrower" | "related"; score: number };
type MappingResult = { suggestions: Mapping[]; confirmed: Mapping | null };

export function DiscoveryReviewPanel({ workspaceId, canEdit, canAdmin }: {
  workspaceId: string; canEdit: boolean; canAdmin: boolean;
}) {
  const qc = useQueryClient();
  const [status, setStatus] = useState<ReviewStatus | "">("");
  const [offset, setOffset] = useState(0);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [reason, setReason] = useState("");
  const [definition, setDefinition] = useState("");
  const [bulkSelected, setBulkSelected] = useState<Record<string, number>>({});
  const [bulkReason, setBulkReason] = useState("");
  const [mappingAi, setMappingAi] = useState(false);
  const [mappingChoice, setMappingChoice] = useState("");
  const closeRef = useRef<HTMLButtonElement>(null);
  const openerRef = useRef<HTMLElement | null>(null);

  const queue = useQuery<Queue>({
    queryKey: ["discovery-review", workspaceId, status, offset],
    queryFn: () => rawFetch<Queue>(`/catalog/discovery/proposals/?workspace=${encodeURIComponent(workspaceId)}&limit=100&offset=${offset}${status ? `&status=${status}` : ""}`),
    enabled: Boolean(workspaceId),
  });
  const detail = useQuery<Proposal>({
    queryKey: ["discovery-review-detail", workspaceId, selectedId],
    queryFn: () => rawFetch<Proposal>(`/catalog/discovery/proposals/${selectedId}/?workspace=${encodeURIComponent(workspaceId)}`),
    enabled: Boolean(workspaceId && selectedId),
  });
  const mappings = useQuery<MappingResult>({
    queryKey: ["discovery-mappings", workspaceId, selectedId, mappingAi],
    queryFn: () => rawFetch(`/catalog/discovery/proposals/${selectedId}/mappings/?workspace=${encodeURIComponent(workspaceId)}${mappingAi ? "&ai=true" : ""}`),
    enabled: Boolean(workspaceId && selectedId && detail.data?.status === "approved"),
  });
  const confirmMapping = useMutation<{ revision: number }, Error, Mapping>({
    mutationFn: (choice) => rawFetch(`/catalog/discovery/proposals/${selectedId}/mappings/`, {
      method: "POST", body: JSON.stringify({
        workspace: workspaceId, pack_id: choice.pack_id,
        concept_iri: choice.concept_iri, relation: choice.relation,
        expected_revision: detail.data?.revision, reason: "semantic_review",
        request_id: crypto.randomUUID(),
      }),
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["discovery-review-detail", workspaceId, selectedId] });
      qc.invalidateQueries({ queryKey: ["discovery-mappings", workspaceId, selectedId] });
      qc.invalidateQueries({ queryKey: ["mapping-summary", workspaceId] });
    },
  });
  const decision = useMutation<Proposal, Error, Decision>({
    mutationFn: (action) => rawFetch<Proposal>(`/catalog/discovery/proposals/${selectedId}/${action}/`, {
      method: "POST",
      body: JSON.stringify({
        workspace: workspaceId, expected_revision: detail.data?.revision,
        request_id: crypto.randomUUID(), reason,
        ...(action === "edit" ? { definition } : {}),
      }),
    }),
    onSuccess: () => {
      setReason("");
      qc.invalidateQueries({ queryKey: ["discovery-review", workspaceId] });
      qc.invalidateQueries({ queryKey: ["discovery-review-detail", workspaceId, selectedId] });
      qc.invalidateQueries({ queryKey: ["discovery-approved-for-draft", workspaceId] });
    },
  });
  const bulkDecision = useMutation<{ count: number }, Error, "approve" | "reject">({
    mutationFn: (action) => rawFetch("/catalog/discovery/proposals/bulk/", {
      method: "POST", body: JSON.stringify({
        workspace: workspaceId, action, proposal_ids: Object.keys(bulkSelected),
        expected_revisions: bulkSelected, reason: bulkReason, request_id: crypto.randomUUID(),
      }),
    }),
    onSuccess: () => {
      setBulkSelected({}); setBulkReason(""); setOffset(0);
      qc.invalidateQueries({ queryKey: ["discovery-review", workspaceId] });
      qc.invalidateQueries({ queryKey: ["approved-claims-for-term-set", workspaceId] });
      qc.invalidateQueries({ queryKey: ["discovery-approved-for-draft", workspaceId] });
    },
  });

  useEffect(() => {
    if (!selectedId) return;
    closeRef.current?.focus();
    const opener = openerRef.current;
    return () => opener?.focus();
  }, [selectedId]);
  useEffect(() => {
    if (!selectedId) return;
    // A decision can remove/disable the focused action button. Keep Escape
    // working even when focus consequently falls outside the drawer.
    const onEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      setSelectedId(null);
    };
    document.addEventListener("keydown", onEscape);
    return () => document.removeEventListener("keydown", onEscape);
  }, [selectedId]);
  useEffect(() => {
    if (detail.data) setDefinition(detail.data.normalized_claim.definition ?? "");
  }, [detail.data]);

  function open(id: string, opener: HTMLElement) {
    openerRef.current = opener;
    setReason("");
    decision.reset();
    setMappingAi(false); setMappingChoice(""); confirmMapping.reset();
    setSelectedId(id);
  }
  function close() {
    setSelectedId(null);
    decision.reset();
  }
  function handleDialogKey(event: React.KeyboardEvent<HTMLDivElement>) {
    if (event.key === "Escape") { event.preventDefault(); close(); return; }
    if (event.key !== "Tab") return;
    const controls = Array.from(event.currentTarget.querySelectorAll<HTMLElement>(
      "button:not([disabled]),textarea:not([disabled]),input:not([disabled]),select:not([disabled])",
    ));
    if (!controls.length) return;
    if (event.shiftKey && document.activeElement === controls[0]) {
      event.preventDefault(); controls[controls.length - 1].focus();
    } else if (!event.shiftKey && document.activeElement === controls[controls.length - 1]) {
      event.preventDefault(); controls[0].focus();
    }
  }

  const proposal = detail.data;
  const reviewable = proposal?.status === "proposed" || proposal?.status === "source_conflict";
  const lastDecision = proposal?.revisions?.filter((item) =>
    item.action === "approved" || item.action === "rejected" || item.action === "conflict_superseded").at(-1);
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-muted">Review cited terms and class properties, then select approved claims into a draft. Mappings are deterministic by default; optional AI uses your workspace key. Nothing publishes automatically.</p>
        <label className="text-xs text-muted">Status
          <select className="input ml-2" value={status}
                  onChange={(event) => { setStatus(event.target.value as ReviewStatus | ""); setOffset(0); }}>
            <option value="">All</option>
            <option value="proposed">Proposed</option>
            <option value="source_conflict">Source conflict</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
            <option value="superseded">Superseded</option>
          </select>
        </label>
      </div>
      {queue.isLoading && <div role="status" className="text-sm text-muted">Loading proposals…</div>}
      {canAdmin && <div className="flex flex-wrap items-center gap-2 border border-border p-2 text-xs">
        <button className="btn" type="button" disabled={!queue.data?.results.some((item) => item.status === "proposed")}
          onClick={() => setBulkSelected((old) => ({ ...old, ...Object.fromEntries(
            (queue.data?.results ?? []).filter((item) => item.status === "proposed")
              .map((item) => [item.id, item.revision]),
          ) }))}>Select proposed on this page</button>
        <button className="btn" type="button" disabled={!Object.keys(bulkSelected).length}
          onClick={() => setBulkSelected({})}>Clear selection</button>
        <span>{Object.keys(bulkSelected).length} selected</span>
        <label>Reason
          <select className="input ml-2" value={bulkReason} onChange={(event) => setBulkReason(event.target.value)}>
            <option value="">Select a reason</option>
            <option value="evidence_verified">Evidence verified</option>
            <option value="duplicate">Duplicate</option>
            <option value="out_of_scope">Out of scope</option>
            <option value="unsupported_claim">Unsupported claim</option>
          </select>
        </label>
        <button className="btn-primary" type="button"
          disabled={!bulkReason || !Object.keys(bulkSelected).length || bulkDecision.isPending}
          onClick={() => bulkDecision.mutate("approve")}>Approve selected</button>
        <button className="btn" type="button"
          disabled={!bulkReason || !Object.keys(bulkSelected).length || bulkDecision.isPending}
          onClick={() => bulkDecision.mutate("reject")}>Reject selected</button>
        {bulkDecision.isError && <ErrorBox error={bulkDecision.error} />}
        {bulkDecision.isSuccess && <span role="status" className="text-good">{bulkDecision.data.count} reviewed.</span>}
      </div>}
      {queue.isError && <ErrorBox error={queue.error} />}
      {queue.data?.results.length === 0 && <EmptyState title="No proposals in this queue" hint="Stage a structured dictionary in Discover terms first." />}
      {queue.data && queue.data.results.length > 0 && (
        <div className="overflow-x-auto rounded border border-border">
          <table className="w-full text-left text-xs">
            <thead className="bg-border/20 text-muted"><tr>
              {canAdmin && <th className="p-3">Select</th>}
              <th className="p-3">Term or property</th><th className="p-3">Type</th>
              <th className="p-3">Status</th><th className="p-3">Source</th><th className="p-3">Review</th>
            </tr></thead>
            <tbody>{queue.data.results.map((item) => (
              <tr key={item.id} className="border-t border-border">
                {canAdmin && <td className="p-3"><input type="checkbox"
                  aria-label={`Select ${item.normalized_claim.name ?? item.kind}`}
                  checked={bulkSelected[item.id] !== undefined} disabled={item.status !== "proposed"}
                  onChange={() => setBulkSelected((old) => {
                    const next = { ...old };
                    if (next[item.id] !== undefined) delete next[item.id];
                    else next[item.id] = item.revision;
                    return next;
                  })} /></td>}
                <td className="p-3 font-medium">{item.normalized_claim.name}</td>
                <td className="p-3">{item.kind.replace("_", " ")}</td>
                <td className="p-3"><StatusPill status={item.status} /></td>
                <td className="p-3 text-muted">{item.source_locator}</td>
                <td className="p-3"><button className="text-accent underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                  onClick={(event) => open(item.id, event.currentTarget)}>Open</button></td>
              </tr>
            ))}</tbody>
          </table>
          <div className="flex items-center justify-between border-t border-border p-2 text-[11px] text-muted">
            <span>Showing {offset + 1}–{offset + queue.data.results.length} of {queue.data.count}</span>
            <div className="flex gap-2">
              <button className="rounded border border-border px-2 py-1 disabled:opacity-40"
                      disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - 100))}>Previous</button>
              <button className="rounded border border-border px-2 py-1 disabled:opacity-40"
                      disabled={offset + 100 >= queue.data.count} onClick={() => setOffset(offset + 100)}>Next</button>
            </div>
          </div>
        </div>
      )}
      {selectedId && (
        <div className="fixed inset-0 z-50 flex justify-end bg-black/60" onMouseDown={(event) => {
          if (event.target === event.currentTarget) close();
        }}>
          <div role="dialog" aria-modal="true" aria-labelledby="review-title"
               onKeyDown={handleDialogKey}
               className="flex h-full w-full max-w-xl flex-col overflow-y-auto border-l border-border bg-bg p-5 text-fg shadow-xl">
            <div className="flex items-start justify-between gap-3">
              <h2 id="review-title" className="text-lg font-medium">Review proposal</h2>
              <button ref={closeRef} onClick={close} aria-label="Close review"
                      className="rounded border border-border px-2 py-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent">Close</button>
            </div>
            {detail.isLoading && <p role="status" className="mt-4 text-sm text-muted">Loading proposal…</p>}
            {detail.isError && <div className="mt-4"><ErrorBox error={detail.error} /></div>}
            {proposal && <div className="mt-5 space-y-4 text-sm">
              <div><p className="text-xs text-muted">Claim</p><p className="font-medium">{proposal.normalized_claim.name}</p></div>
              <div><p className="text-xs text-muted">Status and revision</p><p>{proposal.status.replace("_", " ")} · {proposal.revision}</p></div>
              {!reviewable && (
                <div role="status" className="rounded border border-border bg-border/10 p-3 text-xs text-muted">
                  This proposal has a final decision and is read-only. Stage an updated source to propose a correction.
                  {lastDecision?.reason && <p className="mt-2">Decision reason: {lastDecision.reason.replaceAll("_", " ")}</p>}
                </div>
              )}
              {reviewable && !canEdit && (
                <p role="status" className="text-xs text-muted">An editor or higher can edit this proposal; an admin or owner can approve or reject it.</p>
              )}
              <div><p className="text-xs text-muted">Source evidence · {proposal.source_locator}</p>
                <p className="rounded border border-border bg-border/10 p-3">{proposal.evidence_withheld ? "Evidence withheld" : proposal.redacted_quote}</p>
              </div>
              <div><p className="text-xs text-muted">Definition</p>
                {reviewable && canEdit ? (
                  <textarea aria-label="Definition" className="input mt-1 min-h-28 w-full text-sm" value={definition}
                            onChange={(event) => setDefinition(event.target.value)} maxLength={1000} />
                ) : (
                  <p className="mt-1 whitespace-pre-wrap rounded border border-border bg-border/10 p-3">{proposal.normalized_claim.definition}</p>
                )}
              </div>
              {proposal.status === "approved" && <section className="space-y-2 border-t border-border pt-3" aria-label="Concept mapping">
                <h3 className="text-xs font-semibold">Map to an installed pack concept</h3>
                <p className="text-xs text-muted">Deterministic Spanish/English label and alias matches come first. A reviewer confirms the relation; no mapping is automatic.</p>
                {mappings.data?.confirmed && <p role="status" className="text-xs text-good">Confirmed {mappings.data.confirmed.relation} mapping to {mappings.data.confirmed.concept_iri}</p>}
                {mappings.isLoading && <p className="text-xs text-muted">Finding installed concepts…</p>}
                {mappings.isError && <ErrorBox error={mappings.error} />}
                {mappings.data?.suggestions.length === 0 && <p className="text-xs text-muted">No deterministic match. This term remains new.</p>}
                {mappings.data?.suggestions.map((item) => <label key={`${item.pack_id}:${item.concept_iri}:${item.relation}`}
                  className="flex items-start gap-2 border border-border p-2 text-xs">
                  <input type="radio" name="mapping-choice" disabled={!canAdmin}
                    checked={mappingChoice === `${item.pack_id}:${item.concept_iri}:${item.relation}`}
                    onChange={() => setMappingChoice(`${item.pack_id}:${item.concept_iri}:${item.relation}`)} />
                  <span><strong>{item.label}</strong> · {item.relation} · {item.pack_name} · score {item.score}
                    <span className="block break-all font-mono text-muted">{item.concept_iri}</span></span>
                </label>)}
                {canEdit && <button type="button" className="btn" disabled={mappings.isFetching}
                  onClick={() => { setMappingAi(true); setMappingChoice(""); }}>Suggest with my LLM key</button>}
                {canEdit && <p className="text-[11px] text-muted">Optional AI sends the reviewed term label and installed-pack labels to the workspace's selected provider. It never confirms a mapping.</p>}
                {canAdmin && <button type="button" className="btn-primary"
                  disabled={!mappingChoice || confirmMapping.isPending}
                  onClick={() => {
                    const choice = mappings.data?.suggestions.find((item) =>
                      `${item.pack_id}:${item.concept_iri}:${item.relation}` === mappingChoice);
                    if (choice) confirmMapping.mutate(choice);
                  }}>Confirm mapping</button>}
                {confirmMapping.isError && <ErrorBox error={confirmMapping.error} />}
              </section>}
              {reviewable && canEdit && (
                <label className="block text-xs text-muted">Review reason (required)
                  <select className="input mt-1 w-full text-sm" value={reason}
                          onChange={(event) => setReason(event.target.value)}>
                    <option value="">Select a reason</option>
                    <option value="evidence_verified">Evidence verified</option>
                    <option value="definition_corrected">Definition corrected</option>
                    <option value="source_conflict_resolved">Source conflict resolved</option>
                    <option value="duplicate">Duplicate</option>
                    <option value="out_of_scope">Out of scope</option>
                    <option value="unsupported_claim">Unsupported claim</option>
                  </select>
                </label>
              )}
              {decision.isError && <ErrorBox error={decision.error} />}
              <div className="flex flex-wrap gap-2">
                {canEdit && ["proposed", "source_conflict"].includes(proposal.status) &&
                  <button className="btn-primary" disabled={!reason.trim() || !definition.trim() || decision.isPending}
                          onClick={() => decision.mutate("edit")}>Save edit</button>}
                {canAdmin && proposal.status === "source_conflict" &&
                  <button className="btn-primary" disabled={!reason.trim() || decision.isPending}
                          onClick={() => decision.mutate("resolve_conflict")}>Resolve conflict and keep this claim</button>}
                {canAdmin && proposal.status === "proposed" &&
                  <button className="btn-primary" disabled={!reason.trim() || decision.isPending}
                          onClick={() => decision.mutate("approve")}>Approve</button>}
                {canAdmin && ["proposed", "source_conflict"].includes(proposal.status) &&
                  <button className="rounded border border-bad px-3 py-2 text-bad" disabled={!reason.trim() || decision.isPending}
                          onClick={() => decision.mutate("reject")}>Reject</button>}
              </div>
              {proposal.change_proposal_id && <p className="text-xs text-muted">Approved change recorded. Select it below to preview a draft; nothing was published.</p>}
              <section aria-label="Revision history" className="border-t border-border pt-3">
                <h3 className="font-medium">Revision history</h3>
                <ol className="mt-2 space-y-1 text-xs text-muted">{proposal.revisions?.map((item) =>
                  <li key={item.revision}>#{item.revision} · {item.action} · {item.reason || "Staged"}</li>)}</ol>
              </section>
            </div>}
          </div>
        </div>
      )}
      <DiscoveryDraftPanel key={workspaceId} workspaceId={workspaceId} canEdit={canEdit} canAdmin={canAdmin} />
    </div>
  );
}
